import React, { FC, useMemo, useRef, useState } from 'react';

import { Form, useFormikContext } from 'formik';

import { SearchableSelectType } from '../../../../components/inputs/searchableSelect';
import AampeCreateSelectDropdown from '../../../../components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import AampePopover from '../../../../components/shared/AampePopover';
// Components
import styled from 'styled-components';

// Constants
import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

// Hooks
import useGetGroupTestMessageUsers from '../../../../hooks/contact_notification_token/useGetGroupTestMessageUsers';

// Models
import { FormulaTestMessageFormValues } from 'models/formula.model';

import TestFormulaAttributeFields from './TestFormulaAttributeFields';

interface TestFormulaGroupFormProps {
    providerId?: string;
    handleCreateGroup?: () => void;
    providerAdditionalAttributes: Record<string, string>;
}

const TestFormulaGroupForm: FC<TestFormulaGroupFormProps> = ({
    providerId,
    handleCreateGroup,
    providerAdditionalAttributes,
}) => {
    const { values, handleBlur, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const { data: groupTokens } = useGetGroupTestMessageUsers({
        push_provider_id: providerId,
    });

    const [selectedContactId, setSelectedContactId] = useState<string | null>(
        null,
    );
    const [isAddUserOpen, setIsAddUserOpen] = useState(false);
    const addUserAnchorRef = useRef<HTMLDivElement>(null);

    const notificationTokenGroupOptions = useMemo<SearchableSelectType[]>(
        () =>
            groupTokens
                ? groupTokens.map((token) => ({
                      label: token.name,
                      value: token.id as string,
                  }))
                : [],
        [groupTokens],
    );

    const selectedGroupToken = useMemo(
        () =>
            groupTokens?.find(
                (g) =>
                    g.id ===
                    (values.notification_token as SearchableSelectType)?.value,
            ),
        [groupTokens, values.notification_token],
    );

    const removedUsers = useMemo(
        () =>
            selectedGroupToken?.users?.filter(
                (u) => !(values.users ?? []).find((vu: any) => vu.id === u.id),
            ) ?? [],
        [selectedGroupToken, values.users],
    );

    const contactIds = values.contact_ids ?? [];

    const effectiveSelectedId =
        selectedContactId && contactIds.includes(selectedContactId)
            ? selectedContactId
            : contactIds[0] ?? null;

    const selectedUserIndex = contactIds.indexOf(effectiveSelectedId ?? '');
    const selectedUserLabel =
        selectedUserIndex >= 0
            ? values.users[selectedUserIndex]?.name
            : undefined;

    return (
        <StyledForm>
            <SavedTokenContainer>
                <SavedTokenText>
                    If you have previously saved your token/receiver with a
                    group name you can select it from the dropdown
                </SavedTokenText>
                <FieldGroup>
                    <GroupLabelRow>
                        <span>Group name</span>
                        {handleCreateGroup && (
                            <CreateGroupLink onClick={handleCreateGroup}>
                                <span>Create a new group</span>
                                <ICONS.externalLink size={14} />
                            </CreateGroupLink>
                        )}
                    </GroupLabelRow>
                    <AampeCreateSelectDropdown
                        label=""
                        options={notificationTokenGroupOptions}
                        value={values.notification_token}
                        name={'notification_token'}
                        onChange={(
                            _,
                            value:
                                | SearchableSelectType
                                | SearchableSelectType[],
                        ) => {
                            setFieldValue('notification_token', value);
                            if (!value) {
                                setFieldValue('users', []);
                                setFieldValue('contact_ids', []);
                                setFieldValue(
                                    'integration_attribute_values',
                                    {},
                                );
                                return;
                            }
                            const group = groupTokens?.find(
                                (g) =>
                                    g.id ===
                                    (value as SearchableSelectType)?.value,
                            );
                            setFieldValue('users', group?.users);
                            setFieldValue(
                                'contact_ids',
                                group?.users?.map((u) => u.token),
                            );
                        }}
                        placeholder={'Select group'}
                        onBlur={handleBlur}
                    />
                </FieldGroup>
            </SavedTokenContainer>

            {values.users?.length > 0 && (
                <TabSection>
                    <UserTabsContainer>
                        {values.users.map((user: any, index: number) => {
                            const contactId = contactIds[index];
                            const isSelected =
                                effectiveSelectedId === contactId;
                            return (
                                <UserTab
                                    key={user.id}
                                    isSelected={isSelected}
                                    onClick={() =>
                                        setSelectedContactId(contactId)
                                    }
                                >
                                    <span>{user.name}</span>
                                    <RemoveButton
                                        isSelected={isSelected}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            const updatedUsers =
                                                values.users.filter(
                                                    (u: any) =>
                                                        u.id !== user.id,
                                                );
                                            const updatedContactIds =
                                                values.contact_ids.filter(
                                                    (_, i) => i !== index,
                                                );
                                            const updatedAttributeValues = {
                                                ...values.integration_attribute_values,
                                            };
                                            delete updatedAttributeValues[
                                                contactId
                                            ];

                                            setFieldValue(
                                                'users',
                                                updatedUsers,
                                            );
                                            setFieldValue(
                                                'contact_ids',
                                                updatedContactIds,
                                            );
                                            setFieldValue(
                                                'integration_attribute_values',
                                                updatedAttributeValues,
                                            );

                                            if (isSelected) {
                                                const nextId =
                                                    updatedContactIds[index] ??
                                                    updatedContactIds[
                                                        index - 1
                                                    ] ??
                                                    null;
                                                setSelectedContactId(nextId);
                                            }
                                        }}
                                    >
                                        <ICONS.close size={12} />
                                    </RemoveButton>
                                </UserTab>
                            );
                        })}
                        {removedUsers.length > 0 && (
                            <AddUserTab
                                ref={addUserAnchorRef}
                                onClick={() => setIsAddUserOpen(true)}
                            >
                                + Add user
                            </AddUserTab>
                        )}
                    </UserTabsContainer>
                    {effectiveSelectedId &&
                        Object.keys(providerAdditionalAttributes).length >
                            0 && (
                            <AttributeFieldsPanel>
                                <TestFormulaAttributeFields
                                    contactId={effectiveSelectedId}
                                    userLabel={selectedUserLabel}
                                    providerAdditionalAttributes={
                                        providerAdditionalAttributes
                                    }
                                    noWrapper
                                />
                            </AttributeFieldsPanel>
                        )}
                </TabSection>
            )}

            <AampePopover
                isOpen={isAddUserOpen}
                onClose={() => setIsAddUserOpen(false)}
                triggerRef={addUserAnchorRef}
                showArrow={false}
            >
                <div>
                    {removedUsers.map((user) => (
                        <UserListItem
                            key={user.id}
                            onClick={() => {
                                const updatedUsers = [
                                    ...(values.users ?? []),
                                    user,
                                ];
                                const updatedContactIds = [
                                    ...contactIds,
                                    user.token,
                                ];
                                setFieldValue('users', updatedUsers);
                                setFieldValue('contact_ids', updatedContactIds);
                                setSelectedContactId(user.token);
                                setIsAddUserOpen(false);
                            }}
                        >
                            {user.name}
                        </UserListItem>
                    ))}
                </div>
            </AampePopover>
        </StyledForm>
    );
};

const StyledForm = styled(Form)({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const SavedTokenContainer = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const FieldGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const GroupLabelRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
});

const CreateGroupLink = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontWeight: aampeTheme.weights.medium,
    fontSize: aampeTheme.size.font.default,
});

const SavedTokenText = styled.p({
    lineHeight: aampeTheme.size.lineHeight.default,
    fontWeight: aampeTheme.weights.light,
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
});

const TabSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const UserTabsContainer = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    gap: aampeTheme.spacing4(2),
});

const UserTab = styled.div<{ isSelected: boolean }>(({ isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(2, 3),
    borderRadius: isSelected
        ? `${aampeTheme.borderRadius.default} ${aampeTheme.borderRadius.default} 0 0`
        : aampeTheme.borderRadius.default,
    border: `1px solid ${
        isSelected ? aampeTheme.colors.solidA01 : aampeTheme.colors.bordersWG01
    }`,
    borderBottom: isSelected ? 'none' : undefined,
    marginBottom: isSelected ? '-1px' : undefined,
    position: 'relative' as const,
    zIndex: isSelected ? 1 : 0,
    backgroundColor: isSelected
        ? aampeTheme.colors.solidA01
        : aampeTheme.colors.white,
    color: isSelected ? aampeTheme.colors.white : aampeTheme.colors.neutral09,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.small,
    transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
}));

const AttributeFieldsPanel = styled.div({
    border: `1px solid ${aampeTheme.colors.solidA01}`,
    borderRadius: `0 ${aampeTheme.borderRadius.default} ${aampeTheme.borderRadius.default} ${aampeTheme.borderRadius.default}`,
    padding: aampeTheme.spacing4(4),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const AddUserTab = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(2, 3),
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px dashed ${aampeTheme.colors.bordersWG01}`,
    backgroundColor: aampeTheme.colors.white,
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.small,
});

const RemoveButton = styled.button<{ isSelected: boolean }>(
    ({ isSelected }) => ({
        display: 'flex',
        alignItems: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        color: isSelected
            ? aampeTheme.colors.white
            : aampeTheme.colors.neutral09,
        lineHeight: 0,
        '&:hover': {
            color: isSelected
                ? aampeTheme.colors.white
                : aampeTheme.colors.error500,
        },
    }),
);

const UserListItem = styled.span({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(2),
    borderRadius: aampeTheme.borderRadius.default,
    color: aampeTheme.colors.neutral09,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.default,
    '&:hover': {
        backgroundColor: aampeTheme.colors.interactiveWG01,
    },
});

export default TestFormulaGroupForm;
